import React from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TrendingUp, DollarSign, Percent } from 'lucide-react';

const ProfitAnalysisCard = ({ data }) => {
    const { t } = useTranslation();

    if (!data) return null;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Check if there are recovered expenses to show
    const hasRecoveredExpenses = data.settled_deductible_expenses > 0;

    return (
        <Card className="shadow-sm h-100">
            <Card.Body>
                <div className="d-flex align-items-center mb-3">
                    <TrendingUp size={24} className="text-success me-2" />
                    <h5 className="mb-0">{t('dashboard.profitAnalysis.title')}</h5>
                </div>

                <Row className="g-3">
                    <Col md={4}>
                        <div className="p-3 bg-light rounded h-100">
                            <div className="d-flex align-items-center mb-2">
                                <DollarSign size={18} className="text-secondary me-2" />
                                <small className="text-muted">{t('dashboard.profitAnalysis.totalRevenue')}</small>
                            </div>
                            <h4 className="mb-0 text-dark">{formatCurrency(data.total_revenue)}</h4>
                        </div>
                    </Col>

                    <Col md={4}>
                        <div className="p-3 bg-light rounded h-100">
                            <div className="d-flex align-items-center mb-2">
                                <DollarSign size={18} className="text-danger me-2" />
                                <small className="text-muted">{t('dashboard.profitAnalysis.totalCost')}</small>
                            </div>
                            <h4 className="mb-0 text-danger">{formatCurrency(data.total_cost)}</h4>
                            <small className="text-muted d-block mt-1">
                                {t('productsPage.modal.costLabel')}
                            </small>
                        </div>
                    </Col>

                    <Col md={4}>
                        <div className="p-3 bg-light rounded h-100">
                            <div className="d-flex align-items-center mb-2">
                                <DollarSign size={18} className="text-warning me-2" />
                                <small className="text-muted">{t('dashboard.profitAnalysis.grossProfit')}</small>
                            </div>
                            <h4 className="mb-0 text-warning">{formatCurrency(data.gross_profit)}</h4>
                            <small className="text-muted d-block mt-1">
                                {t('dashboard.profitAnalysis.profitMargin')}: {data.gross_margin}%
                            </small>
                        </div>
                    </Col>

                    <Col md={6}>
                        <div className="p-3 bg-light rounded h-100">
                            <div className="d-flex align-items-center mb-2">
                                <DollarSign size={18} className="text-danger me-2" />
                                <small className="text-muted">{t('dashboard.profitAnalysis.totalExpenses')}</small>
                            </div>
                            <h4 className="mb-0 text-danger">{formatCurrency(data.total_expenses)}</h4>
                            {/* Show recovered expenses info */}
                            {hasRecoveredExpenses && (
                                <small className="text-success d-block mt-1">
                                    {t('dashboard.profitAnalysis.recoveredExpenses') || 'Gastos recuperados (deducidos de proveedores)'}: {formatCurrency(data.settled_deductible_expenses)}
                                </small>
                            )}
                        </div>
                    </Col>

                    <Col md={6}>
                        <div className="p-3 border border-success bg-light rounded h-100">
                            <div className="d-flex align-items-center mb-2">
                                <DollarSign size={18} className="text-success me-2" />
                                <small className="text-success fw-bold">{t('dashboard.profitAnalysis.netProfit')}</small>
                            </div>
                            <h4 className="mb-0 text-success fw-bold">{formatCurrency(data.net_profit)}</h4>
                            <small className="text-success d-block mt-1">
                                {t('dashboard.profitAnalysis.netMargin')}: {data.net_margin}%
                            </small>
                        </div>
                    </Col>
                </Row>

                <div className="mt-3 pt-3 border-top">
                    <small className="text-muted">
                        {t('dashboard.profitAnalysis.description')}
                    </small>
                </div>
            </Card.Body>
        </Card>
    );
};

export default ProfitAnalysisCard;

